import Cookie from 'js-cookie';
import {
  AW_COOKIE,
  GEOLOCATION_COOKIES,
  SESSION_COOKIE,
} from '../constants/metadata';
import { removeCookieEverywhere } from '../helpers/removeCookieEverywhere';
import { getDomain } from '../getters/gameContextGetters';
import { CookieSessionContext } from '../getters/types';
import { jwtDecode } from 'jwt-decode';
import config from '@/config';

export const setCookieSessionContext = (
  cookieSessionContext: Partial<CookieSessionContext>,
  cookieLifetimeInMS?: number,
) => {
  const { authToken, loginToken, sessionId } = cookieSessionContext ?? {};

  const authJwt = authToken?.jwt;

  if (!authJwt || !sessionId) return;

  const userId = jwtDecode<{ sub?: string | number }>(authJwt).sub;

  if (!userId) return;

  setSessionCookie(AW_COOKIE.AUTH_TOKEN, authJwt, cookieLifetimeInMS);
  setSessionCookie(AW_COOKIE.USER_ID, String(userId), cookieLifetimeInMS);
  setSessionCookie(AW_COOKIE.SESSION_ID, sessionId, cookieLifetimeInMS);

  const loginJwt = loginToken?.jwt;
  if (loginJwt) {
    setSessionCookie(AW_COOKIE.LOGIN_TOKEN, loginJwt, cookieLifetimeInMS);
  }
};

export const removeCookieSessionContext = () => {
  removeCookieEverywhere(AW_COOKIE.AUTH_TOKEN);
  removeCookieEverywhere(AW_COOKIE.LOGIN_TOKEN);
  removeCookieEverywhere(AW_COOKIE.SESSION_ID);
  removeCookieEverywhere(GEOLOCATION_COOKIES.X_GEOLOCATION_TOKEN);
  removeCookieEverywhere(AW_COOKIE.USER_ID);
  removeCookieEverywhere(SESSION_COOKIE.START_TIME);
};

/// This function sets the session start time cookie with the provided start time
export const setSessionStartTimeCookie = (startTime) => {
  const cookieDomain = getDomain();
  removeCookieEverywhere(SESSION_COOKIE.START_TIME);
  Cookie.set(SESSION_COOKIE.START_TIME, startTime, { domain: cookieDomain });
};

/**
 * Session cookies can already exist on a parent domain when GW is launched
 * from the lobby or a native wrapper. Clear every visible scope before writing
 * the canonical configured-domain copy to avoid duplicate cookie headers.
 */
export const setSessionCookie = (
  name: string,
  value: string,
  cookieLifetimeInMS?: number,
) => {
  removeCookieEverywhere(name);
  setCookie(name, value, cookieLifetimeInMS);
};

export const setCookie = (
  name: string,
  value: string,
  cookieLifetimeInMS?: number,
  path?: string,
  domain?: string,
  shouldEncode = true,
) => {
  if (typeof document === 'undefined') {
    return;
  }

  const maxAgeAttr: string = !isNaN(Number(cookieLifetimeInMS))
    ? `; max-age=${Math.floor(cookieLifetimeInMS! / 1000)}`
    : '';
  const expiresAttr: string = !isNaN(Number(cookieLifetimeInMS))
    ? `; expires=${new Date(Date.now() + cookieLifetimeInMS!).toUTCString()}`
    : '';
  const pathAttr = `; path=${path ? path : '/'}`;
  const domainAttr = `; domain=${domain ? domain : config.domain}`;

  const cValue: string = value || '';
  const cookieValue: string = shouldEncode
    ? encodeURIComponent(cValue)
    : cValue;

  document.cookie = `${name}=${cookieValue}${maxAgeAttr}${expiresAttr}${pathAttr}${domainAttr}`;
};
